import { markPublicUserCache } from '@/api/public';
import { useSiteRedirect } from '@/hooks/useSiteRedirect/useSiteRedirect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { UseSiteRedirectPopupReturn } from './types/siteRedirect';
import { delay, safeAsync, getSiteRedirectEncodeUrlWithReferrer } from './utils/siteRedirectUtils';
import { useSiteRedirectTrack } from './useSiteRedirectTrack';

/**
 * 国家站切换弹窗逻辑 Hook
 * 处理弹窗的显示、交互和埋点逻辑
 */
export const useSiteRedirectPopup = (): UseSiteRedirectPopupReturn => {
  const [disabled, setDisabled] = useState(false);
  const [checkboxValue, setCheckboxValue] = useState(false);
  const siteRedirectReturn = useSiteRedirect();
  const { siteRedirectInfo } = siteRedirectReturn;

  // 计算弹窗是否可见
  const dialogVisible = useMemo(
    () => !disabled && !!siteRedirectInfo.popStatus && !!siteRedirectInfo.targetUrl,
    [siteRedirectInfo.popStatus, siteRedirectInfo.targetUrl, disabled, siteRedirectInfo],
  );

  // 计算国旗图片
  const fromNationalFlag = siteRedirectInfo.popMsg.from_national_flag || '';
  const toNationalFlag = siteRedirectInfo.popMsg.to_national_flag || '';
  const jumpCountryCode = siteRedirectInfo.popMsg.jump_country_code || '';

  /**
   * 获取提示状态
   */
  const getPromptState = useCallback((): string => {
    /**
     * 1. 未显示：未有显示该提示
     * 2. 显示未勾选，显示了提示框，且未勾选
     * 3. 显示已勾选，显示了提示框，且已勾选
     */
    if (siteRedirectInfo.popStatus === 1) return '未显示';
    if (siteRedirectInfo.popStatus === 2) {
      return checkboxValue ? '显示已勾选' : '显示未勾选';
    }
    return '未显示';
  }, [siteRedirectInfo.popStatus, checkboxValue]);

  const { SD_SwitchSitePopUp_Exp, SD_SwitchSitePopUp_Click } = useSiteRedirectTrack(
    jumpCountryCode,
    siteRedirectInfo.fromTrigger,
    getPromptState,
  );

  /**
   * 标记用户缓存
   */
  const markUserCache = useCallback(async () => {
    if (checkboxValue) {
      await safeAsync(async () => markPublicUserCache());
    }
  }, [checkboxValue]);

  /**
   * 关闭弹窗
   */
  const handleClose = useCallback(async () => {
    SD_SwitchSitePopUp_Click('Close');
    markUserCache();
    setDisabled(true);
  }, [markUserCache, SD_SwitchSitePopUp_Click]);

  /**
   * 确认跳转
   */
  const handleConfirm = useCallback(async () => {
    SD_SwitchSitePopUp_Click(siteRedirectInfo.popMsg.button_copy);
    markUserCache();
    await delay(500);
    window.open(getSiteRedirectEncodeUrlWithReferrer(siteRedirectInfo.targetUrl), '_self');
  }, [
    markUserCache,
    SD_SwitchSitePopUp_Click,
    siteRedirectInfo.popMsg.button_copy,
    siteRedirectInfo.targetUrl,
  ]);

  /**
   * 取消跳转
   */
  const handleCancel = useCallback(async () => {
    SD_SwitchSitePopUp_Click(siteRedirectInfo.popMsg.staying_copy);
    markUserCache();
    setDisabled(true);
  }, [markUserCache, SD_SwitchSitePopUp_Click, siteRedirectInfo.popMsg.staying_copy]);

  /**
   * 处理复选框变化
   */
  const handleCheckboxChange = useCallback(
    (value: boolean) => {
      SD_SwitchSitePopUp_Click(siteRedirectInfo.popMsg.not_reminding);
      setCheckboxValue(value);
    },
    [SD_SwitchSitePopUp_Click, siteRedirectInfo.popMsg.not_reminding],
  );

  useEffect(() => {
    siteRedirectReturn.onSiteRedirectPopupShow(dialogVisible);
  }, [dialogVisible]);

  useEffect(() => {
    dialogVisible && SD_SwitchSitePopUp_Exp();
  }, [dialogVisible, SD_SwitchSitePopUp_Exp]);

  return {
    dialogVisible,
    disabled,
    checkboxValue,
    fromNationalFlag,
    toNationalFlag,
    jumpCountryCode,
    handleClose,
    handleConfirm,
    handleCancel,
    handleCheckboxChange,
    getPromptState,
    ...siteRedirectReturn,
  };
};
